import { useMemo } from 'react';
import { useAuthUser } from './useAuthUser';
import {
    checkPermission,
    getPermissions,
    Permission,
} from '@/types/permissions';

export { Permission };

export const ORG_OWNER_ROLE = 'ORG_OWNER';
export const ORG_USER_MANAGER_ROLE = 'ORG_USER_MANAGER';

const rolePermissionFallback: Record<string, Permission[]> = {
    [ORG_OWNER_ROLE]: [
        Permission.ORGANIZATION_SETTINGS_VIEW,
        Permission.ORGANIZATION_SETTINGS_EDIT,
        Permission.ORGANIZATION_CONFIGURATION_VIEW,
        Permission.ORGANIZATION_CONFIGURATION_EDIT,
    ],
    [ORG_USER_MANAGER_ROLE]: [
        Permission.ORGANIZATION_SETTINGS_VIEW,
        Permission.ORGANIZATION_SETTINGS_EDIT,
        Permission.ORGANIZATION_CONFIGURATION_VIEW,
    ],
};

export function usePermissions() {
    const { user } = useAuthUser();
    const permissionSet = useMemo(() => {
        const mergedPermissions = new Set<Permission>(
            getPermissions(user?.roles ?? []),
        );

        const granted = user?.permissions;
        granted?.forEach((permission) => {
            if (Object.values(Permission).includes(permission as Permission)) {
                mergedPermissions.add(permission as Permission);
            }
        });

        for (const role of user?.roles ?? []) {
            for (const permission of rolePermissionFallback[role] ?? []) {
                mergedPermissions.add(permission);
            }
        }

        return mergedPermissions;
    }, [user?.permissions, user?.roles]);

    return useMemo(
        () => ({
            has: (permission: Permission) => permissionSet.has(permission),
            canPerform: (permission: Permission, isOwner: boolean) =>
                checkPermission(permissionSet, permission, isOwner),
        }),
        [permissionSet],
    );
}
